import {createSelector} from '@reduxjs/toolkit'

import {
    addDate,
    addYears,
    DateFormat,
    differenceInDays,
    differenceInMonths,
    formatDateFns,
    getDateText,
    getMonthDateText,
    rangeDate,
    subDate,
} from '@shared/helpers/date'
import {isEmpty} from '@shared/helpers/lang'

import {CATEGORY_DIVISION_KEY} from '@web/core/constants'
import {extractQueryFromUrl} from '@web/helpers/path'
import {commonSceneInfoSelector} from '@web/slices/common/slice'
import {locationSelector, pageQueryInfoSelector} from '@web/slices/router'
import {
    SaleSettlementDetailCategory,
    saleSettlementPayHoldSelector,
    SaleSettlementStatusCategory,
} from '@web/slices/saleSettlement/slice'

const Duration = {
    start: {day: 14},
    end: {day: 16},
}

const makeDateMinMaxDate = (options?: {duration?: {day: number}}) => {
    return () => {
        const today = new Date()
        const standard = options?.duration ? addDate(today, options?.duration) : today
        const minDate = subDate(standard, {year: 5, day: 16})
        const maxDate = standard
        const minDateText = getDateText(minDate)
        const maxDateText = getDateText(maxDate)

        return {
            minDate,
            minDateText,
            maxDate,
            maxDateText,
            // 5년 이내의 날짜인지
            isValidDate: (date: Date): boolean => {
                const dateText = getDateText(date)
                return minDateText <= dateText && dateText <= maxDateText
            },
            // 선택된 기간이 1년 이내인지
            isValidRange: (startDate: Date, endDate: Date) => {
                return differenceInDays(addYears(new Date(startDate), 1), new Date(endDate)) >= 0
            },
        }
    }
}

const getDateMinMaxDate = makeDateMinMaxDate({
    duration: Duration.end,
})

const makeMonthRange = (duration: {start: {day: number}; end: {day: number}}) => {
    return () => {
        const standard = new Date()
        const {start, end} = rangeDate(standard, duration)
        return {
            start: getDateText(start),
            end: getDateText(end),
            startMonth: getMonthDateText(start),
            endMonth: getMonthDateText(end),
        }
    }
}

const getMonthRange = makeMonthRange(Duration)

const getMonthRangeWithPayHold = makeMonthRange({
    start: {day: 31},
    end: {day: 0},
})

function convertQuery<T extends object>({
    query,
    key: queryKey,
    obj,
}: {
    query: Record<string, string>
    key: string
    obj: T
}): {
    origins?: string[]
    items?: string[]
    valid: boolean
} {
    const origins = query[queryKey] ? query[queryKey].split(CATEGORY_DIVISION_KEY) : undefined
    const items = origins?.filter((code: string) => {
        return Object.values(obj).some((detailCode) => detailCode === code)
    })
    return {
        origins,
        items,
        valid: origins?.length === items?.length,
    }
}

/** Query 에서 Application 이 공통으로 이해할수 있는 값 전달 */
export const saleSettlementPageQueryInfoSelector = createSelector(
    pageQueryInfoSelector,
    locationSelector,
    ({query}, {key}) => {
        const {minDateText, maxDateText, isValidDate, isValidRange} = getDateMinMaxDate()
        const isSearchFilter = query.search === 'filter'

        const pageValid = !!Number(query.page) && Number(query.page) > 0
        const searchValid = ['advanced', 'filter'].includes(query.search)
        const keyword = query.keyword ? `${query.keyword}`.trim() : ''

        const {date, dateValid} = (function () {
            const start = query.startDate ?? ''
            const end = query.endDate ?? ''
            const startDate = start ? new Date(start) : null
            const endDate = end ? new Date(end) : null
            if (
                startDate &&
                endDate &&
                startDate <= endDate &&
                isValidDate(startDate) &&
                isValidDate(endDate) &&
                isValidRange(startDate, endDate)
            ) {
                return {
                    date: {
                        startDateText: getDateText(startDate),
                        endDateText: getDateText(endDate),
                        maxDateText,
                        minDateText,
                    },
                    dateValid: true,
                }
            }
            return {
                date: {
                    startDateText: undefined,
                    endDateText: undefined,
                    minDateText: undefined,
                    maxDateText: undefined,
                },
                dateValid: false,
            }
        })()

        const {items: categories, valid: categoryValid} = convertQuery({
            key: 'category',
            obj: SaleSettlementStatusCategory,
            query,
        })

        const {items: details, valid: detailValid} = convertQuery({
            key: 'detail',
            obj: SaleSettlementDetailCategory,
            query,
        })

        const errors = [
            {
                valid: query.page ? pageValid : true,
                code: 'PAGE',
            },
            {
                valid: query.startDate || query.endDate ? dateValid : true,
                code: 'DATE',
            },
            {valid: query.keyword ? !!keyword : true, code: 'SEARCH'},
            {
                valid: query.category ? categoryValid : true,
                code: 'STATUS',
            },
            {
                valid: query.detail ? detailValid : true,
                code: 'STATUS',
            },
            {valid: query.search ? searchValid : true, code: 'FILTER'},
        ]
            .filter(({valid}) => !valid)
            .map(({code}) => code)

        return {
            key,
            page: pageValid ? query.page : undefined,
            date,
            category: categoryValid ? query.category : undefined,
            categories,
            detail: detailValid ? query.detail : undefined,
            details,
            keyword,
            search: searchValid ? query.search : undefined,
            valid: {
                isError: !isEmpty(errors),
                date: dateValid,
                category: categoryValid,
                detail: detailValid,
                search: searchValid,
                replaceUrl: extractQueryFromUrl(window.location.href, [
                    'page',
                    'keyword',
                    'category',
                    'detail',
                    'search',
                    'startDate',
                    'endDate',
                ]).cleanUrl,
            },
            isSearchFilter,
        }
    },
)

/** 정산 > 현재 검색 상황 */
export const saleSettlementCurrentSearchSelector = createSelector(
    saleSettlementPageQueryInfoSelector,
    ({page, category, keyword, search, categories, detail, details, date}) => {
        return {
            page,
            keyword,
            search,
            category,
            categories,
            detail,
            details,
            startDate: date.startDateText,
            endDate: date.endDateText,
        }
    },
)

/** 정산 > 조회 필드 상황 */
export const saleSettlementSearchFieldSelector = createSelector(
    saleSettlementCurrentSearchSelector,
    ({page, category, detail, keyword, startDate, endDate}) => {
        const {start: initStartDate, end: initEndDate} = getMonthRange()
        return {
            summary: {
                startDate: startDate ?? initStartDate,
                endDate: endDate ?? initEndDate,
            },
            history: {
                startDate: startDate ?? initStartDate,
                endDate: endDate ?? initEndDate,
                category,
                detail,
                keyword,
                page: Number(page) || 1,
            },
        }
    },
)

/** 정산 > 내역 > 검색 */
export const saleSettlementSearchPageSelector = createSelector(
    saleSettlementCurrentSearchSelector,
    ({keyword, startDate, endDate, categories, details}) => {
        const {start: initStartDate, end: initEndDate} = getMonthRange()

        return {
            initData: {
                keyword,
                startDate: startDate ?? initStartDate,
                endDate: endDate ?? initEndDate,
                categories,
                details,
            },
        }
    },
)

/** 정산 > 요약 > 검색창 */
export const saleSettlementSimpleSearchSelector = createSelector(
    saleSettlementCurrentSearchSelector,
    ({startDate, endDate}) => {
        const {start: initStartDate, end: initEndDate} = getMonthRange()

        return {
            startDate: startDate ?? initStartDate,
            endDate: endDate ?? initEndDate,
        }
    },
)

/** 검색 키워드 노출 값 */
export const saleSettlementKeywordSelector = createSelector(
    saleSettlementPageQueryInfoSelector,
    saleSettlementCurrentSearchSelector,
    ({categories, details}, currentSearch) => {
        return {
            keyword: currentSearch.keyword,
            dateText:
                currentSearch.startDate && currentSearch.endDate
                    ? `${formatDateFns(currentSearch.startDate, DateFormat['yyyy. MM. dd.'])} ~ ${formatDateFns(
                          currentSearch.endDate,
                          DateFormat['yyyy. MM. dd.'],
                      )}`
                    : null,
            categories,
            details,
        }
    },
)

/** 정산 > 노출 조건 */
export const saleSettlementPageRenderSelector = createSelector(
    saleSettlementPageQueryInfoSelector,
    commonSceneInfoSelector,
    ({valid, keyword, category, detail, isSearchFilter}, {isRejected}) => {
        const hasKeyword = valid.date || keyword || category || detail

        const shouldKeyword = isSearchFilter ? false : hasKeyword
        const shouldSummary = (isSearchFilter ? true : !hasKeyword) && !isRejected

        return {
            // 키워드 노출 여부
            shouldKeywordsRender: shouldKeyword,
            // 정산 요약 노출 조건
            shouldSummaryRender: shouldSummary,
            // 정산 필터 노출
            shouldFilter: shouldSummary,
            // search Floating Update 조건
            shouldFloatingUpdate: shouldKeyword,
            /** 안내 문구 노출 */
            shouldNoticeRender: !isRejected,
            /** 푸터 노출 여부 */
            shouldFooterRender: !isRejected,
        }
    },
)

function convertDateByPayHold(date: string) {
    const dateValue = date ? new Date(date) : null
    // 달만 계산 하므로 날짜는 초기화
    dateValue?.setDate(1)
    return dateValue
}

export const saleSettlementPayHoldDateSelector = createSelector(
    pageQueryInfoSelector,
    saleSettlementPayHoldSelector,
    ({query}, {selected}) => {
        const {isValidDate} = getDateMinMaxDate()
        const {startMonth: initStartDate, endMonth: initEndDate} = getMonthRangeWithPayHold()
        const defaultDate = {
            startDate: initStartDate,
            endDate: initEndDate,
        }
        const {startDateText, endDateText, isError} = (function () {
            const startDate = convertDateByPayHold(query.startDate ?? '')
            const endDate = convertDateByPayHold(query.endDate ?? '')

            if (startDate || endDate) {
                const valid =
                    startDate &&
                    endDate &&
                    startDate <= endDate &&
                    isValidDate(startDate) &&
                    isValidDate(endDate) &&
                    !(differenceInMonths(addYears(startDate, 1), endDate) < 1)
                return {
                    isError: !valid,
                    startDateText: valid ? getMonthDateText(startDate) : undefined,
                    endDateText: valid ? getMonthDateText(endDate) : undefined,
                }
            }
            return {
                isError: false,
                startDateText: undefined,
                endDateText: undefined,
            }
        })()
        return {
            valid: {
                isError,
                replaceUrl: extractQueryFromUrl(window.location.href, ['startDate', 'endDate']).cleanUrl,
            },
            initDate: {
                startDate: startDateText || defaultDate.startDate,
                endDate: endDateText || defaultDate.endDate,
            },
            startDate: selected.startDate || startDateText || defaultDate.startDate,
            endDate: selected.endDate || endDateText || defaultDate.endDate,
        }
    },
)
